
// src/utils/simTrackCanvas.js
// Replaces prior implementation — robust, snapshot-based sampling with fallbacks
console.log("%c[simTrackCanvas] loaded (modal-canvas - improved)", "color: #4CAF50");

let simCanvas = null;
let simCtx = null;
let trackImg = null; // ImageData snapshot (if possible)
let lastWidth = 0;
let lastHeight = 0;
let ready = false;
let lastInitAttempt = 0;
const getSimGridSize = () => {
    try {
        const store =
            window.__REDUX_STORE__ ||
            window.reduxStore ||
            window.store ||
            window.__store;
        const state = store?.getState?.();
        const width = Math.max(1, Math.floor(state?.scene?.simGridWidth ?? 20));
        const height = Math.max(1, Math.floor(state?.scene?.simGridHeight ?? 15));
        return { width, height };
    } catch (err) {
        return { width: 20, height: 15 };
    }
};

/**
 * Try to find the #sim-canvas in the DOM and initialize contexts.
 * Retries until found (modal may mount after app start).
 */
export function initSimTrackCanvas() {
    try {
        if (typeof window === "undefined") return;
        const now = Date.now();
        // avoid extremely tight retry loops
        if (now - lastInitAttempt < 100) return;
        lastInitAttempt = now;

        const tryInit = () => {
            const c = document.getElementById("sim-canvas");
            if (c && c.getContext) {
                simCanvas = c;
                try {
                    simCtx = simCanvas.getContext("2d", { willReadFrequently: true });
                } catch (e) {
                    console.warn("[simTrackCanvas] getContext with willReadFrequently failed, falling back", e);
                    simCtx = simCanvas.getContext("2d");
                }

                if (!simCtx) {
                    console.error("[simTrackCanvas] failed to get 2d context");
                    return;
                }

                lastWidth = simCanvas.width || 0;
                lastHeight = simCanvas.height || 0;
                ready = true;

                // try to populate an initial raster
                try {
                    refreshTrackRaster();
                } catch (e) {
                    console.warn("[simTrackCanvas] initial refreshTrackRaster failed", e);
                }

                console.log("[simTrackCanvas] init ok ->", !!simCanvas, "ctx:", !!simCtx, "size:", lastWidth + "x" + lastHeight);
                return;
            }

            // not found yet — try again shortly
            setTimeout(tryInit, 150);
        };

        tryInit();
    } catch (err) {
        console.error("[simTrackCanvas] init error:", err);
    }
}

/**
 * Create/update an internal ImageData snapshot of the canvas.
 * If canvas is cross-origin-tainted, snapshot creation will throw; we catch that
 * and keep trackImg = null (in which case we will read from context per-sample).
 *
 * Returns true on success (snapshot created or context available), false on failure.
 */
export function refreshTrackRaster() {
    try {
        if (!simCanvas || !simCtx) {
            initSimTrackCanvas();
            console.warn("[simTrackCanvas] refresh: canvas not ready yet");
            return false;
        }

        // If canvas size changed, update stored sizes
        if (simCanvas.width !== lastWidth || simCanvas.height !== lastHeight) {
            lastWidth = simCanvas.width;
            lastHeight = simCanvas.height;
        }

        // Try to take a snapshot (fast for many reads)
        try {
            trackImg = simCtx.getImageData(0, 0, simCanvas.width, simCanvas.height);
            // mark ready
            ready = true;
            // small debug
            // console.log("[simTrackCanvas] raster snapshot created", simCanvas.width, simCanvas.height);
            return true;
        } catch (snapshotErr) {
            // Possibly canvas is tainted (cross-origin). Fall back to null snapshot and read on-demand.
            console.warn("[simTrackCanvas] could not snapshot canvas (possible CORS taint). Will fallback to direct reads. Error:", snapshotErr);
            trackImg = null;
            ready = true; // still consider ready because simCtx exists
            return true;
        }
    } catch (err) {
        console.error("[simTrackCanvas] refreshTrackRaster error:", err);
        ready = false;
        return false;
    }
}

/**
 * Internal helper: safely read a single pixel from the canvas.
 * x/y are canvas pixel coordinates.
 * Returns { r, g, b, a } or null on failure.
 */
function readPixelFromCanvasXY(canvasX, canvasY) {
    if (!simCanvas || !simCtx) return null;

    // clamp coordinates inside canvas bounds
    const cx = Math.max(0, Math.min(simCanvas.width - 1, Math.round(canvasX)));
    const cy = Math.max(0, Math.min(simCanvas.height - 1, Math.round(canvasY)));

    try {
        if (trackImg && trackImg.data) {
            const idx = (cy * trackImg.width + cx) * 4;
            const d = trackImg.data;
            return { r: d[idx], g: d[idx + 1], b: d[idx + 2], a: d[idx + 3] };
        } else {
            // fallback: read directly from context
            const im = simCtx.getImageData(cx, cy, 1, 1).data;
            return { r: im[0], g: im[1], b: im[2], a: im[3] };
        }
    } catch (err) {
        console.error("[simTrackCanvas] readPixelFromCanvasXY error:", err);
        return null;
    }
}

// Read color under robot or at a grid coordinate.
// gridX/gridY coordinates in simulator grid-space.
// Accepts fractional grid coordinates (your runScript passes floats).
// Returns { r, g, b, a } with numeric 0-255 values. If not ready, returns white.
export function getColorUnderRobot(gridX, gridY) {
    try {
        if (!simCanvas || !simCtx) {
            initSimTrackCanvas();
            console.warn('simTrackCanvas getColorUnderRobot: canvas/context not ready yet');
            return { r: 255, g: 255, b: 255, a: 255 };
        }

        console.log(`🎯 Sampling at GRID (${gridX}, ${gridY})`);
        console.log(`📐 Canvas size: ${simCanvas.width} x ${simCanvas.height}`);

        const { width: GRIDW, height: GRIDH } = getSimGridSize();

        let gx = Number(gridX);
        let gy = Number(gridY);

        if (!Number.isFinite(gx) || !Number.isFinite(gy)) {
            return { r: 255, g: 255, b: 255, a: 255 };
        }

        // ✅ FIX: Add 0.5 to sample at cell CENTER
        const cellCenterX = (gx + 0.5) / GRIDW;
        const cellCenterY = (gy + 0.5) / GRIDH;

        const canvasX = cellCenterX * simCanvas.width;
        const canvasY = cellCenterY * simCanvas.height;

        console.log(`📍 Grid (${gx}, ${gy}) → Canvas pixels (${canvasX}, ${canvasY})`);

        return readPixelFromCanvasXY(canvasX, canvasY) || { r: 255, g: 255, b: 255, a: 255 };

    } catch (err) {
        console.error('simTrackCanvas getColorUnderRobot error:', err);
        return { r: 255, g: 255, b: 255, a: 255 };
    }
}

/**
 * Convenience: sample RGB at given grid coordinates and return hex string.
 */
export function rgbToHex({ r = 255, g = 255, b = 255 } = {}) {
    const toHex = n => ("0" + Math.max(0, Math.min(255, Math.round(n))).toString(16)).slice(-2);
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Public helper: returns '#RRGGBB' for given grid coords (grid-space).
 * Accepts fractional coords.
 */
export function sampleHexAt(gridX, gridY) {
    const col = getColorUnderRobot(gridX, gridY);
    return rgbToHex(col);
}

/**
 * Public helper: returns {r,g,b,a} for given grid coords.
 */
export function sampleRGBAt(gridX, gridY) {
    return getColorUnderRobot(gridX, gridY);
}

// Auto-init in case module is imported after modal mount
if (typeof window !== "undefined") {
    window.addEventListener("load", () => {
        setTimeout(initSimTrackCanvas, 200);
    });

    // Expose functions for debugging
    window.simTrackCanvas = window.simTrackCanvas || {};
    window.simTrackCanvas.initSimTrackCanvas = initSimTrackCanvas;
    window.simTrackCanvas.refreshTrackRaster = refreshTrackRaster;
    window.simTrackCanvas.getColorUnderRobot = getColorUnderRobot;
    window.simTrackCanvas.rgbToHex = rgbToHex;
    window.simTrackCanvas.sampleHexAt = sampleHexAt;
    window.simTrackCanvas.sampleRGBAt = sampleRGBAt;
    window.simTrackCanvas._debug = {
        get simCanvas() { return simCanvas; },
        get simCtx() { return simCtx; },
        get trackImg() { return trackImg; },
        get lastSize() { return { w: lastWidth, h: lastHeight }; },
        ready: () => ready
    };
}

export default {
    initSimTrackCanvas,
    refreshTrackRaster,
    getColorUnderRobot,
    rgbToHex,
    sampleHexAt,
    sampleRGBAt
};

